import { BrowserRouter, Routes, Route, Navigate, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth, apiRequest } from "./context/AuthContext";
import { ThemeProvider, useTheme, DIVISION_THEME } from "./context/ThemeContext";
import { CoachAuthProvider, useCoachAuth } from "./context/CoachAuthContext";
import VideoIntro from "./components/VideoIntro";
import CoachVerificationPage from "./pages/admin/CoachVerificationPage";
import CoachAuthCallback from "./pages/coach/CoachAuthCallback";
import CoachLandingPage from "./pages/coach/CoachLandingPage";
import CoachLoginPage from "./pages/coach/CoachLoginPage";
import CoachRegisterPage from "./pages/coach/CoachRegisterPage";
import CoachDashboard from "./pages/coach/CoachDashboard";
import CoachPlayersPage from "./pages/coach/CoachPlayersPage";
import CoachPlayerProfile from "./pages/coach/CoachPlayerProfile";
import CoachBoardPage from "./pages/coach/CoachBoardPage";
import CoachSettingsPage from "./pages/coach/CoachSettingsPage";
import CoachNotificationsPage from "./pages/coach/CoachNotificationsPage";
import CoachMessagesPage from "./pages/coach/CoachMessagesPage";
import CoachProgramPage from "./pages/coach/CoachProgramPage";
import PlayerMessagesPage from "./pages/PlayerMessagesPage";
import ProgrammesPage from "./pages/ProgrammesPage";
import Dashboard from "./pages/Dashboard";
import CollegesPage from "./pages/CollegesPage";
import CollegeDetailPage from "./pages/CollegeDetailPage";
import CommunicationsPage from "./pages/CommunicationsPage";
import ComposePage from "./pages/ComposePage";
import StrategyPage from "./pages/StrategyPage";
import NCAACHeckerPage from "./pages/NCAACHeckerPage";
import GpaCalculatorPage from "./pages/GpaCalculatorPage";
import LearnPage from "./pages/LearnPage";
import LearnModulePage from "./pages/LearnModulePage";
import ArrivalHubPage from "./pages/ArrivalHubPage";
import ProfilePage from "./pages/ProfilePage";
import ResponseTrackerPage from "./pages/ResponseTrackerPage";
import AIMatchPage from "./pages/AIMatchPage";
import ComparePage from "./pages/ComparePage";
import LoginPage from "./pages/LoginPage";
import AuthCallback from "./pages/AuthCallback";
import LandingPage from "./pages/LandingPage";
import OnboardingPage from "./pages/OnboardingPage";
import AdminPage from "./pages/AdminPage";
import AdminLoginPage from "./pages/AdminLoginPage";
import AdminUserDetailPage from "./pages/AdminUserDetailPage";
import PricingPage from "./pages/PricingPage";
import SupabaseAuthCallback from "./pages/SupabaseAuthCallback";
import LegalPage from "./pages/LegalPage";
import HelpWidget from "./components/HelpWidget";
import { Trophy, Home, BookOpen, Mail, Wand2, Lightbulb, Menu, X, ShieldCheck, UserCircle, MessageSquare, LogOut, ChevronDown, Sparkles, Bell, CreditCard, Inbox, School, GraduationCap, Library, Plane } from "lucide-react";

const PRIMARY_NAV = [
  { path: "/dashboard", label: "Dashboard", icon: Home },
  { path: "/messages", label: "Messages", icon: Inbox },
  { path: "/compose", label: "Compose", icon: Wand2 },
  { path: "/communications", label: "Emails", icon: Mail },
  { path: "/colleges", label: "Colleges", icon: BookOpen },
  { path: "/responses", label: "Responses", icon: MessageSquare },
  { path: "/profile", label: "Profile", icon: UserCircle },
  { path: "/pricing", label: "Pricing", icon: CreditCard },
];

const SECONDARY_NAV = [
  { path: "/ai-match", label: "AI Match", icon: Sparkles },
  { path: "/strategy", label: "Strategy", icon: Lightbulb },
  { path: "/ncaa", label: "NCAA Check", icon: ShieldCheck },
  { path: "/gpa-calculator", label: "GPA Calc", icon: GraduationCap },
  { path: "/learn", label: "Learn", icon: Library },
  { path: "/arrival", label: "Your Journey", icon: Plane },
];

const ALL_NAV_ITEMS = [...PRIMARY_NAV, ...SECONDARY_NAV];

function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-100 transition-colors"
        data-testid="user-menu-btn"
      >
        {user?.picture ? (
          <img src={user.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
        ) : (
          <div className="w-7 h-7 rounded-full bg-orange-500 flex items-center justify-center text-white text-xs font-bold">
            {user?.name?.[0]?.toUpperCase() || "U"}
          </div>
        )}
        <span className="hidden md:block text-xs font-semibold text-slate-700 max-w-24 truncate">{user?.name?.split(" ")[0]}</span>
        <ChevronDown className="w-3.5 h-3.5 text-slate-400 hidden md:block" />
      </button>
      {open && (
        <div className="absolute right-0 top-10 w-48 bg-white border border-slate-200 rounded-xl shadow-lg z-50 py-1">
          <div className="px-4 py-2.5 border-b border-slate-100">
            <p className="text-xs font-bold text-slate-800 truncate">{user?.name}</p>
            <p className="text-xs text-slate-400 truncate">{user?.email}</p>
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      )}
    </div>
  );
}

function NotificationBell() {
  const [notifs, setNotifs]   = useState([]);
  const [unread, setUnread]   = useState(0);
  const [open, setOpen]       = useState(false);

  useEffect(() => {
    const load = () => apiRequest("get", "/notifications")
      .then(r => { setNotifs(r.data.notifications || []); setUnread(r.data.unread || 0); })
      .catch(() => {});
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, []);

  const markAll = async () => {
    await apiRequest("post", "/notifications/read-all").catch(() => {});
    setNotifs(prev => prev.map(n => ({ ...n, read: true })));
    setUnread(0);
  };

  return (
    <div className="relative">
      <button data-testid="notification-bell" onClick={() => setOpen(!open)}
        className="relative p-2 text-slate-600 hover:text-slate-900 transition-colors rounded-lg hover:bg-slate-100">
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-10 w-80 bg-white border border-slate-200 rounded-2xl shadow-xl z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <p className="text-xs font-black uppercase tracking-wider text-slate-700">Notifications</p>
            {unread > 0 && (
              <button onClick={markAll} className="text-xs text-orange-500 font-bold hover:text-orange-700">Mark all read</button>
            )}
          </div>
          <div className="max-h-72 overflow-y-auto">
            {notifs.length === 0 ? (
              <div className="py-8 text-center text-slate-400 text-xs">No notifications yet</div>
            ) : notifs.map(n => (
              <div key={n.id} data-testid={`notif-${n.id}`}
                className={`px-4 py-3 border-b border-slate-50 last:border-0 ${!n.read ? "bg-orange-50/60" : ""}`}>
                <p className="text-xs font-bold text-slate-800">{n.college_name || "Update"}</p>
                <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">{n.message}</p>
                <p className="text-xs text-slate-400 mt-1">{n.status ? `Status: ${n.status}` : ""}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AppLayout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const location = useLocation();
  const { division, setDivision } = useTheme();
  const theme = DIVISION_THEME[division] || DIVISION_THEME.mens;
  const moreRef = useRef(null);

  // Poll unread message count every 60s
  useEffect(() => {
    const fetchUnread = () =>
      apiRequest("get", "/player/messages/unread-count")
        .then(r => setUnreadMessages(r.data?.unread || 0))
        .catch(() => {});
    fetchUnread();
    const iv = setInterval(fetchUnread, 60000);
    return () => clearInterval(iv);
  }, []);

  // Reset count when user visits /messages
  useEffect(() => {
    if (location.pathname === "/messages") setUnreadMessages(0);
  }, [location.pathname]);

  // Close "More" dropdown on route change
  useEffect(() => { setMoreOpen(false); }, [location.pathname]);

  // Close "More" dropdown on outside click
  useEffect(() => {
    if (!moreOpen) return;
    const handler = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [moreOpen]);

  const isSecondaryActive = SECONDARY_NAV.some(item => location.pathname.startsWith(item.path));

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: "var(--font-body, Manrope, sans-serif)" }}>
      {/* Top Nav */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center gap-4">
          {/* Logo */}
          <NavLink to="/dashboard" className="flex items-center gap-2 flex-shrink-0 mr-2">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: theme.accent }}>
              <Trophy className="w-4 h-4 text-white" />
            </div>
            <span className="font-black text-slate-900 text-sm" style={{ fontFamily: "var(--font-heading, 'Barlow Condensed', sans-serif)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              CourtBound
            </span>
            {division === "womens" && (
              <span className="hidden sm:inline text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: theme.accentLight, color: theme.accentText }}>Women's</span>
            )}
          </NavLink>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1 flex-1">
            {PRIMARY_NAV.map(({ path, label, icon: Icon }) => (
              <NavLink
                key={path}
                to={path}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    isActive ? "font-bold" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }`
                }
                style={({ isActive }) => isActive ? { background: theme.accentLight, color: theme.accentText } : {}}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
                {path === "/messages" && unreadMessages > 0 && (
                  <span data-testid="messages-unread-badge" className="ml-0.5 bg-red-500 text-white text-xs font-black rounded-full px-1.5 py-0.5 leading-none min-w-[18px] text-center">
                    {unreadMessages > 9 ? "9+" : unreadMessages}
                  </span>
                )}
              </NavLink>
            ))}
            {/* More ▾ dropdown */}
            <div className="relative" ref={moreRef}>
              <button
                data-testid="more-nav-btn"
                onClick={() => setMoreOpen(prev => !prev)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  isSecondaryActive ? "font-bold" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`}
                style={isSecondaryActive ? { background: theme.accentLight, color: theme.accentText } : {}}
              >
                More
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${moreOpen ? "rotate-180" : ""}`} />
              </button>
              {moreOpen && (
                <div data-testid="more-nav-dropdown" className="absolute left-0 top-10 w-48 bg-white border border-slate-200 rounded-xl shadow-lg z-50 py-1">
                  {SECONDARY_NAV.map(({ path, label, icon: Icon }) => (
                    <NavLink
                      key={path}
                      to={path}
                      className={({ isActive }) =>
                        `flex items-center gap-2 px-4 py-2.5 text-xs font-semibold transition-colors ${
                          isActive ? "font-bold" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                        }`
                      }
                      style={({ isActive }) => isActive ? { color: theme.accentText } : {}}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {label}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <NotificationBell />
            <UserMenu />
            <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden p-2 text-slate-600">
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile nav */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-slate-100 bg-white px-4 py-3 grid grid-cols-2 gap-1">
            {ALL_NAV_ITEMS.map(({ path, label, icon: Icon }) => (
              <NavLink
                key={path}
                to={path}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold ${
                    isActive ? "font-bold" : "text-slate-600 hover:bg-slate-50"
                  }`
                }
                style={({ isActive }) => isActive ? { background: theme.accentLight, color: theme.accentText } : {}}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
                {path === "/messages" && unreadMessages > 0 && (
                  <span className="bg-red-500 text-white text-xs font-black rounded-full px-1.5 py-0.5 leading-none min-w-[18px] text-center">
                    {unreadMessages > 9 ? "9+" : unreadMessages}
                  </span>
                )}
              </NavLink>
            ))}
          </div>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {children}
      </main>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-t-transparent division-spinner" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function ProtectedAppRoute({ children, needsOnboarding }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-t-transparent division-spinner" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // null = profile check still in-flight; hold here to prevent race-condition flash of dashboard
  if (needsOnboarding === null) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-t-transparent division-spinner" />
      </div>
    );
  }
  if (needsOnboarding) return <Navigate to="/onboarding" replace />;
  return <AppLayout>{children}</AppLayout>;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <CoachAuthProvider>
            <AppRouter />
            <Toaster position="top-right" richColors />
          </CoachAuthProvider>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

function CoachProtectedRoute({ children }) {
  const { coach, loading } = useCoachAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-600 border-t-transparent" />
      </div>
    );
  }
  if (!coach) return <Navigate to="/coach/login" replace />;
  return children;
}

function LandingPageWithIntro() {
  const SESSION_KEY = "cb_intro_seen";
  const videoExists = true; // set false to disable intro globally
  const [showIntro, setShowIntro] = useState(
    videoExists && !sessionStorage.getItem(SESSION_KEY)
  );
  return (
    <>
      {showIntro && <VideoIntro onComplete={() => setShowIntro(false)} />}
      <LandingPage />
    </>
  );
}

function AppRouter() {
  const { user, loading } = useAuth();
  const location = useLocation();
  // null = profile check in-flight (unknown). true/false = resolved.
  const [needsOnboarding, setNeedsOnboarding] = useState(null);

  useEffect(() => {
    // Still exchanging the session (AuthCallback is rendered below instead
    // of the routed app) — leave needsOnboarding at whatever it currently is
    // (null on first run) so ProtectedAppRoute's loading-state branch holds
    // the screen instead of us prematurely resolving "onboarding not
    // needed". Once AuthCallback finishes and navigates to /dashboard, the
    // pathname change below re-fires this effect on a clean state (hash
    // gone, user settled) and the real profile check runs.
    if (location.hash?.includes("session_id=")) { return; }
    if (!user) { setNeedsOnboarding(false); return; }

    // Check the profile, with one automatic retry after 500 ms.
    // Retry handles the race window where the profile row is still being written
    // immediately after signup. On both attempts:
    //   - profile exists with basketball_gender → go to dashboard
    //   - profile missing or incomplete    → go to onboarding
    //   - API error                         → go to onboarding (never fail open)
    const fetchProfile = () => apiRequest("get", "/profile");

    const decide = (r) => {
      const profileDone = r.data?.full_name && r.data?.basketball_gender;
      setNeedsOnboarding(!profileDone);
    };

    fetchProfile()
      .then(r => {
        if (r.data?.full_name && r.data?.basketball_gender) {
          // Profile is complete — resolve immediately, no retry needed.
          setNeedsOnboarding(false);
        } else {
          // Profile is missing or incomplete on first attempt.
          // Wait 500 ms and retry once before routing to onboarding.
          // This covers the race window right after new-user signup.
          setTimeout(() => {
            fetchProfile()
              .then(decide)
              .catch(() => setNeedsOnboarding(true)); // still failing → route to onboarding
          }, 500);
        }
      })
      .catch(() => {
        // First attempt errored — retry after 500 ms.
        setTimeout(() => {
          fetchProfile()
            .then(decide)
            .catch(() => setNeedsOnboarding(true)); // two failures → onboarding
        }, 500);
      });
  }, [user?.user_id, location.pathname]); // eslint-disable-line

  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  const onboardingDone = () => setNeedsOnboarding(false);

  return (
    <>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/" element={
          loading ? null :
          user ? <Navigate to="/dashboard" replace /> : <LandingPageWithIntro />
        } />
        <Route path="/onboarding" element={
          <ProtectedRoute>
            <OnboardingPage onComplete={onboardingDone} />
          </ProtectedRoute>
        } />
        <Route path="/dashboard" element={<ProtectedAppRoute needsOnboarding={needsOnboarding}><Dashboard /></ProtectedAppRoute>} />
        <Route path="/colleges" element={<ProtectedAppRoute needsOnboarding={needsOnboarding}><CollegesPage /></ProtectedAppRoute>} />
        <Route path="/colleges/:id" element={<ProtectedAppRoute needsOnboarding={needsOnboarding}><CollegeDetailPage /></ProtectedAppRoute>} />
        <Route path="/communications" element={<ProtectedAppRoute needsOnboarding={needsOnboarding}><CommunicationsPage /></ProtectedAppRoute>} />
        <Route path="/messages" element={<ProtectedAppRoute needsOnboarding={needsOnboarding}><PlayerMessagesPage /></ProtectedAppRoute>} />
        <Route path="/compose" element={<ProtectedAppRoute needsOnboarding={needsOnboarding}><ComposePage /></ProtectedAppRoute>} />
        <Route path="/strategy" element={<ProtectedAppRoute needsOnboarding={needsOnboarding}><StrategyPage /></ProtectedAppRoute>} />
        <Route path="/ncaa" element={<ProtectedAppRoute needsOnboarding={needsOnboarding}><NCAACHeckerPage /></ProtectedAppRoute>} />
        <Route path="/gpa-calculator" element={<ProtectedAppRoute needsOnboarding={needsOnboarding}><GpaCalculatorPage /></ProtectedAppRoute>} />
        <Route path="/learn" element={<ProtectedAppRoute needsOnboarding={needsOnboarding}><LearnPage /></ProtectedAppRoute>} />
        <Route path="/learn/:slug" element={<ProtectedAppRoute needsOnboarding={needsOnboarding}><LearnModulePage /></ProtectedAppRoute>} />
        <Route path="/arrival" element={<ProtectedAppRoute needsOnboarding={needsOnboarding}><ArrivalHubPage /></ProtectedAppRoute>} />
        <Route path="/profile" element={<ProtectedAppRoute needsOnboarding={needsOnboarding}><ProfilePage /></ProtectedAppRoute>} />
        <Route path="/responses" element={<ProtectedAppRoute needsOnboarding={needsOnboarding}><ResponseTrackerPage /></ProtectedAppRoute>} />
        <Route path="/ai-match" element={<ProtectedAppRoute needsOnboarding={needsOnboarding}><AIMatchPage /></ProtectedAppRoute>} />
        <Route path="/compare" element={<ProtectedAppRoute needsOnboarding={needsOnboarding}><ComparePage /></ProtectedAppRoute>} />
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/admin/users/:userId" element={<AdminUserDetailPage />} />
        <Route path="/admin/coach-verification" element={<CoachVerificationPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/privacy" element={<LegalPage type="privacy" />} />
        <Route path="/terms" element={<LegalPage type="terms" />} />
        <Route path="/auth/callback" element={<SupabaseAuthCallback />} />
        {/* Coach Portal */}
        <Route path="/coach" element={<CoachLandingPage />} />
        <Route path="/coach/login" element={<CoachLoginPage />} />
        <Route path="/coach/register" element={<CoachRegisterPage />} />
        <Route path="/coach/dashboard" element={<CoachProtectedRoute><CoachDashboard /></CoachProtectedRoute>} />
        <Route path="/coach/players" element={<CoachProtectedRoute><CoachPlayersPage /></CoachProtectedRoute>} />
        <Route path="/coach/players/:userId" element={<CoachProtectedRoute><CoachPlayerProfile /></CoachProtectedRoute>} />
        <Route path="/coach/board" element={<CoachProtectedRoute><CoachBoardPage /></CoachProtectedRoute>} />
        <Route path="/coach/settings" element={<CoachProtectedRoute><CoachSettingsPage /></CoachProtectedRoute>} />
        <Route path="/coach/messages" element={<CoachProtectedRoute><CoachMessagesPage /></CoachProtectedRoute>} />
        <Route path="/coach/notifications" element={<CoachProtectedRoute><CoachNotificationsPage /></CoachProtectedRoute>} />
        <Route path="/coach/program/:slug" element={<CoachProgramPage />} />
        <Route path="/programmes" element={<ProgrammesPage />} />
        <Route path="/coach/auth/callback" element={<CoachAuthCallback />} />
      </Routes>
      <HelpWidget />
    </>
  );
}

export default App;
